  import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  Transition,
  TransitionChild,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { Fragment } from "react";
import { Button } from "components/ui";
import Select from "react-select";
import { convertLeadToJob, getConvertJobPrefill } from "services/jobsService";
import { getApplicationForms } from "services/paymentServices";
import { getLeadStageFieldHistory, bulkUpdateLeadStage } from "services/leadsService";
import { getUserIdFromToken, getCompanyIdFromToken } from "utils/tokenUtils";
import { toast } from "sonner";
import clsx from "clsx";
import {
  CheckCircleIcon,
  XMarkIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  SparklesIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

import CreateJobFromScratchForm from "./CreateJobFromScratchForm";
import { invalidateNotesCache } from "hooks";

const CREATION_METHODS = [
  {
    key: "scratch",
    label: "From scratch",
    description:
      "Build the job from the ground up with all fields pre-filled from this lead.",
    Icon: PencilSquareIcon,
  },
  // {
  //   key: "template",
  //   label: "From template",
  //   description:
  //     "Start from a pre-defined job template and apply lead data on top.",
  //   Icon: SparklesIcon,
  // },
];

export function ConvertToJobModal({ show, onClose, leadData, onReverted, leadContacts }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState("scratch");
  const [selectedFormId, setSelectedFormId] = useState("");
  const [jobForms, setJobForms] = useState([]);
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [isLoadingPrefill, setIsLoadingPrefill] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const autoCloseTimer = useRef(null);
  const leadid = leadData?.id || leadData?.LeadId;
  useEffect(() => {
    if (show) {
      if (selectedMethod === "template") {
        fetchJobForms();
      } else if (selectedMethod === "scratch") {
        fetchPrefillData();
      }
    }
  }, [show, selectedMethod, leadid]);

  const fetchPrefillData = async () => {
    const leadId = leadData?.id || leadData?.LeadId;
    if (!leadId) return;
    setIsLoadingPrefill(true);
    try {
      const res = await getConvertJobPrefill(leadId);
      if (res.success) {
        setPrefillData(res.data);
      } else {
        toast.error("Failed to fetch prefill details");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching prefill details");
    } finally {
      setIsLoadingPrefill(false);
    }
  };

  const fetchJobForms = async () => {
    setIsLoadingForms(true);
    try {
      const res = await getApplicationForms();
      if (res.success) {
        setJobForms(res.data);
      } else {
        toast.error("Failed to fetch templates");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching templates");
    } finally {
      setIsLoadingForms(false);
    }
  };

  useEffect(() => {
    if (!show) {
      setStep(1);
      setSelectedMethod("scratch");
      setSelectedFormId("");
      setPrefillData(null);
      setHasError(false);
      setShowCancelWarning(false);
      clearTimeout(autoCloseTimer.current);
    }
  }, [show]);

  const handleClose = () => {
    clearTimeout(autoCloseTimer.current);
    setStep(1);
    setSelectedMethod("scratch");
    setSelectedFormId("");
    setPrefillData(null);
    setHasError(false);
    setShowCancelWarning(false);
    onClose();
  };

  const requestClose = () => {
    setShowCancelWarning(true);
  };

  const [isReverting, setIsReverting] = useState(false);

  const handleCancelAndRevert = async () => {
    const leadId = leadData?.id || leadData?.LeadId;
    if (!leadId) {
      toast.error("Lead ID is missing");
      handleClose();
      return;
    }

    setIsReverting(true);
    try {
      const historyRes = await getLeadStageFieldHistory(leadId);
      if (historyRes.success && historyRes.data && historyRes.data.length > 0) {
        const mostRecent = historyRes.data[0];
        const prevStageId = mostRecent.previousStageId || mostRecent.PreviousStageId;
        const prevSubStageId = mostRecent.previousSubStageId || mostRecent.PreviousSubStageId;

        if (prevStageId) {
          const revertResult = await bulkUpdateLeadStage({
            leadIds: [leadId],
            stageId: prevStageId,
            subStageId: prevSubStageId || null,
            remarks: "Stage change reverted due to job conversion cancellation",
          });

          if (revertResult.success) {
            toast.success("Lead restored to its previous stage successfully");
            if (onReverted) {
              onReverted();
              invalidateNotesCache(leadId);
            }
          } else {
            toast.error(revertResult.error || "Failed to revert Lead stage");
          }
        } else {
          toast.warning("No previous stage found in history to revert to");
        }
      } else {
        toast.warning("No stage history found to determine previous stage");
      }
    } catch (error) {
      console.error("Error during stage reversion:", error);
      toast.error("An error occurred while reverting Lead stage");
    } finally {
      setIsReverting(false);
      setShowCancelWarning(false);
      onClose();
    }
  };
  const handleConvert = async () => {
    if (selectedMethod === "template" && !selectedFormId) {
      toast.error("Please select a template");
      return;
    }

    setIsConverting(true);
    setHasError(false);
    try {
      const payload = {
        companyId: getCompanyIdFromToken(),
        userId: getUserIdFromToken(),
        leadId: leadData?.id || leadData?.LeadId,
        creationMethod: 2, // 2 = template
        formId: selectedFormId,
      };

      const result = await convertLeadToJob(payload);

      if (result.success) {
        toast.success(result.message || "Converted to job successfully");
        setStep("success");

        // Try to extract the created JobId from api response
        const rawResData = result.data;
        let jobObj = rawResData?.data || rawResData;
        if (typeof jobObj === "string") {
          try { jobObj = JSON.parse(jobObj); } catch (e) {}
        }
        if (Array.isArray(jobObj) && jobObj.length > 0) {
          jobObj = jobObj[0];
        }
        let createdJobId = null;
        if (jobObj && typeof jobObj === "object") {
          createdJobId = jobObj.JobId || jobObj.jobId || jobObj.id || jobObj.Id;
        } else if (typeof jobObj === "string" && jobObj.length > 5) {
          createdJobId = jobObj;
        }

        autoCloseTimer.current = setTimeout(() => {
          handleClose();
          if (createdJobId) {
            navigate(`/jobs/${createdJobId}`);
          } else {
            navigate("/jobs");
          }
        }, 2500);
      } else {
        toast.error(result.message || "Failed to convert lead to job");
        setHasError(true);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error converting to job");
      setHasError(true);
    } finally {
      setIsConverting(false);
    }
  };

  const handleScratchSubmit = (jobDetails, apiResponse) => {
    setIsConverting(false);
    setHasError(false);
    setStep("success");

    // Try to extract the created JobId from api response
    const rawResData = apiResponse;
    let jobObj = rawResData?.data || rawResData;
    if (typeof jobObj === "string") {
      try { jobObj = JSON.parse(jobObj); } catch (e) {}
    }
    if (Array.isArray(jobObj) && jobObj.length > 0) {
      jobObj = jobObj[0];
    }
    let createdJobId = null;
    if (jobObj && typeof jobObj === "object") {
      createdJobId = jobObj.JobId || jobObj.jobId || jobObj.id || jobObj.Id;
    } else if (typeof jobObj === "string" && jobObj.length > 5) {
      createdJobId = jobObj;
    }

    autoCloseTimer.current = setTimeout(() => {
      handleClose();
      if (createdJobId) {
        navigate(`/jobs/${createdJobId}`);
      } else {
        navigate("/jobs");
      }
    }, 2500);
  };

  // Helper parsing logic for step 1 & 2 info preview
  let leadInfoParsed = null;
  if (leadData?.leadinfo) {
    try {
      leadInfoParsed =
        typeof leadData.leadinfo === "string"
          ? JSON.parse(leadData.leadinfo)
          : leadData.leadinfo;
    } catch (e) {
      // ignore
    }
  }

  let leadContact = leadData?.LeadContact || leadData?.LeadContacts;
  if (!leadContact && leadInfoParsed) {
    leadContact = leadInfoParsed.LeadContact || leadInfoParsed.LeadContacts;
  }
  if (typeof leadContact === "string" && leadContact) {
    try {
      leadContact = JSON.parse(leadContact);
    } catch (e) {
      // ignore
    }
  }
  const primaryContact =
    leadContacts && leadContacts.length > 0
      ? leadContacts[0]
      : Array.isArray(leadContact) && leadContact.length > 0
        ? leadContact[0]
        : null;
  const contactName = primaryContact
    ? primaryContact.Fullname ||
      primaryContact.FullName ||
      primaryContact.fullName ||
      primaryContact.name
    : "";
  const contactPhone = primaryContact
    ? primaryContact.PhoneNumber || primaryContact.phoneNumber
    : "";
  const contactEmail = primaryContact
    ? primaryContact.Email || primaryContact.email
    : "";

  const getDynamicFieldValue = (fieldName) => {
    if (!leadData?.DynamicFields) return null;
    try {
      const fields =
        typeof leadData.DynamicFields === "string"
          ? JSON.parse(leadData.DynamicFields)
          : leadData.DynamicFields;
      const field = fields.find(
        (f) =>
          f.FieldName === fieldName ||
          f.FieldName === fieldName.replace(/_/g, ""),
      );
      return field ? field.FieldValue : null;
    } catch (e) {
      return null;
    }
  };

  const dynAddress =
    getDynamicFieldValue("address") || getDynamicFieldValue("street_address") || getDynamicFieldValue("street_address_cont") || getDynamicFieldValue("address_cont");
  const dynSuburb =
    getDynamicFieldValue("suburb") ||
    getDynamicFieldValue("city") ||
    getDynamicFieldValue("town") ||
    getDynamicFieldValue("suburb_cont") ||
    getDynamicFieldValue("city_cont");
  const dynState = getDynamicFieldValue("state") || getDynamicFieldValue("state_cont");
  const dynPostalCode =
    getDynamicFieldValue("postal_code") ||
    getDynamicFieldValue("postcode") ||
    getDynamicFieldValue("postalCode") ||
    getDynamicFieldValue("postCode") ||
    getDynamicFieldValue("postal_code_cont") ||
    getDynamicFieldValue("postcode_cont");

  const dynTitle = getDynamicFieldValue("title") || getDynamicFieldValue("Title") || getDynamicFieldValue("job_title");
  const rawTitle = leadData?.LeadRawJsonData?.find((item) => item.MasterFieldName === "title")?.Value;

  let prefillObj = prefillData?.data || prefillData;
  if (typeof prefillObj === "string") {
    try { prefillObj = JSON.parse(prefillObj); } catch (e) {}
  }
  if (prefillObj?.data || prefillObj?.Data) {
    prefillObj = prefillObj.data || prefillObj.Data;
  }

  const validLeadDataName = leadData?.name !== "Unknown" ? leadData?.name : null;
  const validLeadDataFullName = leadData?.FullName !== "Unknown" ? leadData?.FullName : null;

  const name =
    dynTitle ||
    rawTitle ||
    prefillObj?.Title ||
    prefillObj?.title ||
    prefillObj?.JobTitle ||
    prefillObj?.jobTitle ||
    leadData?.title ||
    leadData?.Title ||
    validLeadDataFullName ||
    validLeadDataName ||
    leadData?.Name ||
    leadInfoParsed?.title ||
    leadInfoParsed?.Title ||
    "Unknown";
  const address =
    leadData?.address ||
    dynAddress ||
    leadInfoParsed?.address ||
    leadInfoParsed?.Address ||
    "";
  const suburb =
    leadData?.suburb ||
    leadData?.city ||
    dynSuburb ||
    leadInfoParsed?.suburb ||
    leadInfoParsed?.suburbName ||
    leadInfoParsed?.city ||
    "";
  const state =
    leadData?.state ||
    dynState ||
    leadInfoParsed?.state ||
    leadInfoParsed?.stateName ||
    "";
  const postalCode =
    leadData?.postalCode ||
    leadData?.postCode ||
    dynPostalCode ||
    leadInfoParsed?.postalCode ||
    leadInfoParsed?.postCode ||
    "";
  const clientName =
    leadData?.clients || contactName || leadInfoParsed?.FullName || name;
  const clientPhone =
    leadData?.phone ||
    leadData?.clientPhone ||
    leadData?.mobile ||
    contactPhone ||
    leadInfoParsed?.PhoneNumber ||
    leadInfoParsed?.phone ||
    "";
  const clientEmail =
    leadData?.email ||
    leadData?.clientEmail ||
    contactEmail ||
    leadInfoParsed?.Email ||
    leadInfoParsed?.email ||
    "";
  const siteLocation = [suburb, state, postalCode].filter(Boolean).join(", ");

  let ownerName = "";
  const internalUsers = prefillObj?.InternalUsers || prefillObj?.internalUsers || prefillObj?.users || prefillObj?.Users;
  if (Array.isArray(internalUsers) && internalUsers.length > 0) {
    ownerName = internalUsers
      .map((u) => u.UserName || u.userName || u.label || u.name || u.FullName || u.fullName)
      .filter(Boolean)
      .join(", ");
  } else if (leadData?.assignedOwner) {
    ownerName = leadData.assignedOwner;
  } else if (leadData?.owner) {
    ownerName = leadData.owner;
  } else if (leadData?.Owners) {
    let ownersArr = leadData.Owners;
    if (typeof ownersArr === "string") {
      try {
        ownersArr = JSON.parse(ownersArr);
      } catch (e) {
        // ignore
      }
    }
    if (Array.isArray(ownersArr)) {
      ownerName = ownersArr
        .map((o) => o.OwnerName || o.name || o.FullName)
        .filter(Boolean)
        .join(", ");
    }
  }

  const sourceName =
    leadData?.SourceName ||
    leadData?.sourceName ||
    leadData?.Medium ||
    leadData?.medium ||
    leadData?.Sources ||
    leadData?.source ||
    leadInfoParsed?.SourceName ||
    leadInfoParsed?.sourceName ||
    leadInfoParsed?.Medium ||
    leadInfoParsed?.medium ||
    leadInfoParsed?.Sources ||
    leadInfoParsed?.source ||
    "";
  const priority =
    leadData?.Priority ||
    leadData?.priority ||
    leadData?.priorityLabel ||
    leadInfoParsed?.Priority ||
    leadInfoParsed?.priority ||
    leadInfoParsed?.priorityLabel ||
    "";

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[350] flex items-center justify-center p-4"
        onClose={step === "success" ? () => {} : requestClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel
            className={clsx(
              "dark:bg-dark-800 dark:border-dark-600 relative flex w-full transform flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl transition-all duration-300",
              step === 3 ? "h-[90vh] max-w-4xl" : "max-h-[95vh] max-w-lg",
            )}
          >
            {/* ── STEP 1: Carry-over data review ── */}
            {step === 1 && (
              <>
                <div className="dark:border-dark-600 flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
                  <div>
                    <DialogTitle className="text-base font-bold text-gray-900 dark:text-white">
                      Convert to Job
                    </DialogTitle>
                    <p className="dark:text-dark-400 mt-0.5 text-xs text-gray-500">
                      Review the data that will carry over from this lead.
                    </p>
                  </div>
                  <button
                    onClick={requestClose}
                    className="dark:hover:text-dark-200 shrink-0 text-gray-400 transition-colors hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 py-5">
                  <div className="dark:border-dark-700 dark:from-dark-850 dark:to-dark-800 flex items-start gap-4 rounded-2xl border border-gray-100 bg-gradient-to-r from-gray-50 to-white p-4 shadow-sm">
                    <div className="bg-primary-50 dark:bg-primary-950/35 text-primary-600 dark:text-primary-400 border-primary-100/70 dark:border-primary-900/35 flex size-12 shrink-0 items-center justify-center rounded-full border text-lg font-bold">
                      {name ? name?.charAt(0).toUpperCase() : "?"}
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="dark:text-dark-500 text-[10px] font-bold tracking-wider text-gray-400 uppercase">
                        Lead Title
                      </p>
                      <h4 className="truncate text-base font-bold text-gray-900 dark:text-white">
                        {name || "—"}
                      </h4>
                    </div>
                  </div>

                  <div className="dark:border-dark-700 dark:bg-dark-800 space-y-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <p className="dark:text-dark-500 text-[10px] font-bold tracking-wider text-gray-400 uppercase">
                      Lead details
                    </p>
                    <div className="flex flex-wrap gap-x-8 gap-y-4">
                      <div className="min-w-[140px] flex-1 space-y-1">
                        <span className="dark:text-dark-500 block text-[10px] font-medium text-gray-400">
                          ASSIGNED OWNER
                        </span>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="dark:bg-dark-700 dark:text-dark-300 flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600">
                            {ownerName
                              ? ownerName?.charAt(0).toUpperCase()
                              : "O"}
                          </div>
                          <span
                            className="dark:text-dark-100 text-xs font-semibold truncate text-gray-800"
                            title={ownerName || "—"}
                          >
                            {ownerName || "—"}
                          </span>
                        </div>
                      </div>

                      <div className="min-w-[80px] space-y-1">
                        <span className="dark:text-dark-500 block text-[10px] font-medium text-gray-400">
                          PRIORITY
                        </span>
                        <div>
                          {priority ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                priority.toLowerCase() === "high" ||
                                priority.toLowerCase() === "hot"
                                  ? "border border-red-100 bg-red-50 text-red-600 dark:border-red-900/30 dark:bg-red-950/20 dark:text-red-400"
                                  : priority.toLowerCase() === "medium" ||
                                      priority.toLowerCase() === "warm"
                                    ? "border border-amber-100 bg-amber-50 text-amber-600 dark:border-amber-900/30 dark:bg-amber-950/20 dark:text-amber-400"
                                    : "border border-green-100 bg-green-50 text-green-600 dark:border-green-900/30 dark:bg-green-950/20 dark:text-green-400"
                              }`}
                            >
                              {priority}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </div>

                      <div className="min-w-[100px] space-y-1">
                        <span className="dark:text-dark-500 block text-[10px] font-medium text-gray-400">
                          SOURCE
                        </span>
                        <div>
                          {sourceName ? (
                            <span className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-0.5 text-[10px] font-bold text-indigo-600 dark:border-indigo-900/30 dark:bg-indigo-950/20 dark:text-indigo-400">
                              {sourceName}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>



                  <div className="flex items-start gap-3.5 rounded-2xl border border-blue-100/70 bg-blue-50/50 p-4 dark:border-blue-900/30 dark:bg-blue-950/15">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-blue-100/80 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
                      <DocumentTextIcon className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="text-[10px] font-bold tracking-wider text-blue-500 uppercase dark:text-blue-400">
                        Documents
                      </p>
                      <p className="text-xs leading-normal text-blue-700/90 dark:text-blue-300">
                        All attached documents will carry over to the new job
                        automatically.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="dark:border-dark-600 flex items-center justify-between border-t border-gray-100 px-6 py-4">
                  <div className="flex items-center gap-1.5">
                    <StepDot active />
                    <StepDot />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outlined"
                      color="neutral"
                      onClick={requestClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="filled"
                      color="primary"
                      onClick={() => setStep(2)}
                      className="flex items-center gap-2"
                    >
                      Next
                      <ArrowRightIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* ── STEP 2: Job creation method ── */}
            {step === 2 && (
              <>
                <div className="dark:border-dark-600 flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
                  <div>
                    <DialogTitle className="text-base font-bold text-gray-900 dark:text-white">
                      Select Creation Method
                    </DialogTitle>
                    <p className="dark:text-dark-400 mt-0.5 text-xs text-gray-500">
                      How would you like to create this job?
                    </p>
                  </div>
                  <button
                    onClick={requestClose}
                    className="dark:hover:text-dark-200 shrink-0 text-gray-400 transition-colors hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-3 px-6 py-5">
                  {CREATION_METHODS.map(({ key, label, description, Icon }) => {
                    const selected = selectedMethod === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedMethod(key)}
                        className={`flex w-full flex-col items-start gap-3 rounded-xl border-2 px-4 py-4 text-left transition-all ${
                          selected
                            ? "border-primary bg-primary-50/10 dark:bg-primary-900/20 dark:border-primary-400"
                            : "dark:border-dark-600 dark:hover:border-dark-500 dark:bg-dark-800 border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <div className="flex w-full items-start gap-3">
                          <div
                            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                              selected
                                ? "bg-primary text-white"
                                : "dark:bg-dark-700 dark:text-dark-400 bg-gray-100 text-gray-500"
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <p
                              className={`text-sm leading-none font-bold ${
                                selected
                                  ? "text-primary dark:text-primary-300"
                                  : "dark:text-dark-100 text-gray-800"
                              }`}
                            >
                              {label}
                            </p>
                            <p className="dark:text-dark-400 mt-1 text-xs leading-relaxed text-gray-500">
                              {description}
                            </p>
                          </div>
                          {selected && (
                            <CheckCircleIcon className="text-primary dark:text-primary-400 mt-0.5 h-5 w-5 shrink-0" />
                          )}
                        </div>
                        {selected && key === "template" && (
                          <div
                            className="mt-2 w-full pl-11"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isLoadingForms ? (
                              <p className="text-primary-600 dark:text-primary-400 text-xs">
                                Loading templates...
                              </p>
                            ) : (
                              <Select
                                options={jobForms.map((f) => ({
                                  value: f.FormId || f.formId,
                                  label: f.Title || f.title || f.Name || f.name,
                                }))}
                                value={
                                  selectedFormId
                                    ? jobForms
                                        .map((f) => ({
                                          value: f.FormId || f.formId,
                                          label:
                                            f.Title ||
                                            f.title ||
                                            f.Name ||
                                            f.name,
                                        }))
                                        .find(
                                          (opt) => opt.value === selectedFormId,
                                        )
                                    : null
                                }
                                onChange={(option) =>
                                  setSelectedFormId(option?.value || "")
                                }
                                placeholder="Select a job template"
                                className="react-select-container w-full"
                                classNamePrefix="react-select"
                                isClearable={false}
                              />
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="dark:border-dark-600 flex items-center justify-between border-t border-gray-100 px-6 py-4">
                  <div className="flex items-center gap-1.5">
                    <StepDot />
                    <StepDot active />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outlined"
                      color="neutral"
                      onClick={() => setStep(1)}
                      className="flex items-center gap-2"
                    >
                      <ArrowLeftIcon className="h-3.5 w-3.5" />
                      Back
                    </Button>
                    <Button
                      variant="filled"
                      color="primary"
                      disabled={
                        !selectedMethod ||
                        isConverting ||
                        isLoadingPrefill ||
                        (selectedMethod !== "scratch" && hasError)
                      }
                      onClick={() => {
                        if (selectedMethod === "scratch") {
                          setStep(3);
                        } else {
                          handleConvert();
                        }
                      }}
                      className="flex items-center gap-2"
                    >
                      {selectedMethod === "scratch"
                        ? isLoadingPrefill
                          ? "Loading..."
                          : "Next"
                        : isConverting
                          ? "Converting..."
                          : "Convert to Job"}
                      {selectedMethod === "scratch" && !isLoadingPrefill && (
                        <ArrowRightIcon className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* ── STEP 3: Render CreateJobFromScratchForm ── */}
            {step === 3 && (
              <CreateJobFromScratchForm
                leadData={leadData}
                prefillData={prefillData}
                onSubmit={handleScratchSubmit}
                onBack={() => setStep(2)}
                onClose={requestClose}
                isConverting={isConverting}
                hasError={hasError}
              />
            )}

            {/* ── SUCCESS SCREEN ── */}
            {step === "success" && (
              <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
                <div className="bg-success-50 dark:bg-success-900/20 animate-in zoom-in-50 mb-5 flex h-16 w-16 items-center justify-center rounded-full duration-300">
                  <CheckCircleIcon className="text-success dark:text-success-400 h-9 w-9" />
                </div>
                <h3 className="animate-in fade-in text-lg font-bold text-gray-900 duration-300 dark:text-white">
                  Job Created Successfully
                </h3>
                <p className="dark:text-dark-400 animate-in fade-in mt-2 max-w-xs text-sm leading-relaxed text-gray-500 duration-500">
                  <span className="dark:text-dark-200 font-semibold text-gray-700">
                    {name}
                  </span>{" "}
                  has been converted and is now visible in Jobs Listing.
                </p>
                <p className="dark:text-dark-500 animate-in fade-in mt-5 text-[11px] text-gray-400 duration-700">
                  This window will close automatically…
                </p>
              </div>
            )}
          </DialogPanel>
        </TransitionChild>
      </Dialog>

      {/* Cancel Warning Modal */}
      <Transition appear show={showCancelWarning} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setShowCancelWarning(false)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all dark:bg-dark-800">
                  <DialogTitle as="h3" className="text-lg font-bold leading-6 text-gray-900 dark:text-white">
                    Cancel Job Conversion
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-dark-300">
                      Are you sure you want to cancel the job conversion?
                      <br /><br />
                      If you continue, the Lead will be moved back to its previous stage and the current "Contract Won/Lost" stage change will be reverted. Any unfinished conversion progress will be discarded.
                    </p>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <Button variant="flat" onClick={() => setShowCancelWarning(false)} disabled={isReverting}>
                      Keep Editing
                    </Button>
                    <Button variant="filled" color="error" onClick={handleCancelAndRevert} disabled={isReverting}>
                      {isReverting ? "Reverting..." : "Yes, Revert Stage"}
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Transition>
  );
}

function StepDot({ active }) {
  return (
    <div
      className={`h-2 rounded-full transition-all duration-300 ${
        active
          ? "bg-primary dark:bg-primary-400 w-5"
          : "dark:bg-dark-600 w-2 bg-gray-200"
      }`}
    />
  );
}
